// ============================================================
//  BENITO ANTONIO — cart-drawer.js
//  Dawn 15.4.1 — Cart Drawer personalizado
// ============================================================

// ── CLASE ORIGINAL DE DAWN (sin tocar) ──────────────────────

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Arrancar el contador de inmediato (sin esperar al fin de la animación),
    // así el tiempo nunca se queda "congelado" al abrir el carrito.
    const cdUrgEl = this.querySelector('#cd-urgency');
    if (cdUrgEl) { cdGetDeadline(); cdStartCountdown(); }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        // ── Iniciar funcionalidades custom al abrir ──
        cdOnOpen();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Guardar posición de scroll para restaurar al cerrar (fix iOS)
    document.body.style.top = `-${window.scrollY}px`;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    // Restaurar scroll position (fix iOS)
    const scrollY = document.body.style.top;
    document.body.classList.remove('overflow-hidden');
    document.body.style.top = '';
    if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);

    // ── Limpiar al cerrar ──
    cdOnClose();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer-CartItems' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  // ── OVERRIDE del update de Dawn ───────────────────────────
  // En lugar de dejar que Dawn reemplace el contenedor entero (lo que
  // reconstruía el slider de recomendados y rompía el estado vacío),
  // hacemos una actualización quirúrgica vía cdApplyCartState:
  //   • solo se refresca la lista de productos + subtotal
  //   • el slider "Hechos para ti" NO se toca (no se reinicia)
  //   • si el carrito queda vacío, se pinta la pantalla de vacío
  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Drawer-quantity-${line}`) ||
          document.getElementById(`Quantity-${line}`);

        // Error (p.ej. sin stock) → revertir cantidad y avisar
        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          this.disableLoading(line);
          return;
        }

        const isEmpty = cdApplyCartState(parsedState);

        if (!isEmpty) {
          this.updateLiveRegions(line, '');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
        }

        this.disableLoading(line);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((o) => o.classList.add('hidden'));
        const errors =
          document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
        if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// ============================================================
//  FUNCIONALIDADES CUSTOM
// ============================================================

// ── COUNTDOWN ───────────────────────────────────────────────
// El contador PERSISTE entre recargas: guardamos la hora de FIN
// (timestamp en ms) en localStorage. Así corre de forma continua
// desde que se añade el primer artículo y NO se reinicia al refrescar.
const CD_DURATION    = 5 * 60; // 5 minutos en segundos
const CD_STORAGE_KEY = 'cd_deadline';
let cdInterval       = null;

function cdGetDeadline() {
  // Devuelve el timestamp (ms) de fin; lo crea la primera vez.
  let dl = parseInt(localStorage.getItem(CD_STORAGE_KEY) || '0', 10);
  if (!dl || isNaN(dl)) {
    dl = Date.now() + CD_DURATION * 1000;
    try { localStorage.setItem(CD_STORAGE_KEY, String(dl)); } catch (e) {}
  }
  return dl;
}

function cdClearDeadline() {
  try { localStorage.removeItem(CD_STORAGE_KEY); } catch (e) {}
}

function cdRemainingSeconds() {
  return Math.max(0, Math.round((cdGetDeadline() - Date.now()) / 1000));
}

function cdOnOpen() {
  // Countdown — solo si no se arrancó ya en open() (evita reiniciar el tick)
  const urgency = document.getElementById('cd-urgency');
  if (urgency && !cdInterval) {
    cdGetDeadline(); // crea el deadline si aún no existe (primer artículo)
    cdStartCountdown();
  }

  // Barra de descuento por cantidad
  cdInitIncentiveBar();

  // Slider
  cdInitSlider();

  // Listener variantes
  cdInitVariantListeners();

  // Listener botones añadir
  cdInitAddButtons();

  // Clic en imagen → ir al producto con la selección
  cdInitImageLinks();
}

function cdOnClose() {
  cdStopCountdown();
  cdStopSlider();
}

function cdStartCountdown() {
  cdStopCountdown();
  cdUpdateDisplay(cdRemainingSeconds());

  // Cada 500 ms: aunque un tick se retrase, el valor se recalcula desde el
  // deadline real, por lo que el reloj nunca se percibe congelado.
  cdInterval = setInterval(() => {
    cdUpdateDisplay(cdRemainingSeconds());
  }, 500);
}

function cdStopCountdown() {
  if (cdInterval) { clearInterval(cdInterval); cdInterval = null; }
}

function cdUpdateDisplay(seconds) {
  const el = document.getElementById('cd-countdown');
  if (!el) return;
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  el.textContent = `${m}:${s}`;

  const urgency = document.getElementById('cd-urgency');
  if (urgency) {
    if (seconds <= 0) {
      // Llegó a 0 → rojo elegante parpadeante (se queda así)
      urgency.classList.remove('is-urgent');
      urgency.classList.add('cd-ended');
      cdStopCountdown();
    } else {
      urgency.classList.remove('cd-ended');
      // Parpadeo urgente en el último minuto (igual que antes)
      urgency.classList.toggle('is-urgent', seconds < 60);
    }
  }
}

// ── BARRA DE DESCUENTO POR CANTIDAD ──────────────────────────
// Hitos reales de la tienda: 2 art. → 10% · 3 art. → 15% · 4+ art. → 20%
const CD_INCENTIVE_TIERS = [
  { qty: 2, pct: 10 },
  { qty: 3, pct: 15 },
  { qty: 4, pct: 20 },
];

function cdInitIncentiveBar() {
  const box = document.getElementById('cd-incentive');
  if (!box) return;
  const qty = parseInt(box.dataset.count || '0', 10);
  const fillEl = document.getElementById('cd-incentive-fill');

  // Barrido de entrada: la línea SIEMPRE arranca en 0 y se desliza hasta
  // el valor real cada vez que se abre el carrito, para que se note
  // claramente que es dinámica y no un estado fijo.
  if (fillEl) {
    fillEl.style.transition = 'none';
    fillEl.style.width = '0%';
    void fillEl.offsetWidth; // fuerza reflow: aplica el 0% antes de animar
    fillEl.style.transition = '';
  }

  // Doble requestAnimationFrame: garantiza que el navegador PINTE el
  // fotograma en 0% antes de aplicar el valor real (en Safari/iOS un solo
  // rAF puede no bastar y la transición no llega a verse).
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      cdUpdateIncentiveBar(qty);
    });
  });
}

// Rellena la línea hasta el punto EXACTO de cada hito (16.667% / 50% /
// 83.333%, igual que la posición real de los puntos en el HTML), e
// interpola de forma continua entre hitos para que el movimiento sea
// fluido según se añaden o quitan artículos. Se garantiza un mínimo
// visible en cuanto hay 1 artículo, para que la línea nunca se vea vacía.
function cdComputeIncentiveFill(qty) {
  // Blindaje: cualquier valor no numérico (undefined, NaN...) se trata
  // como "sin datos" y no rellena nada, en vez de saltar al último hito.
  if (typeof qty !== 'number' || isNaN(qty)) return 0;

  const qtyBreaks   = [0, 2, 3, 4];
  const widthBreaks = [0, 100 / 6, 50, 500 / 6]; // 0, 16.667, 50, 83.333

  if (qty <= 0) return 0;
  if (qty >= qtyBreaks[3]) return widthBreaks[3];

  let raw = 0;
  for (let i = 0; i < 3; i++) {
    const q0 = qtyBreaks[i], q1 = qtyBreaks[i + 1];
    if (qty <= q1) {
      const w0 = widthBreaks[i], w1 = widthBreaks[i + 1];
      const frac = (qty - q0) / (q1 - q0);
      raw = w0 + frac * (w1 - w0);
      break;
    }
  }
  return Math.max(raw, 4); // mínimo visible garantizado con >=1 artículo
}

function cdUpdateIncentiveBar(qty) {
  const box = document.getElementById('cd-incentive');
  if (!box) return;

  box.dataset.count = qty;

  const textEl = document.getElementById('cd-incentive-text');
  const fillEl = document.getElementById('cd-incentive-fill');
  const dots   = box.querySelectorAll('.cd-incentive__dot');

  const fillPct = cdComputeIncentiveFill(qty);
  if (fillEl) fillEl.style.width = fillPct + '%';

  dots.forEach((dot, i) => {
    const tier = CD_INCENTIVE_TIERS[i];
    if (tier) dot.classList.toggle('is-reached', qty >= tier.qty);
  });

  const lastTier  = CD_INCENTIVE_TIERS[CD_INCENTIVE_TIERS.length - 1];
  const nextTier  = CD_INCENTIVE_TIERS.find((t) => qty < t.qty); // undefined si ya está al máximo
  const recTitleEl = document.getElementById('cd-rec-title');

  // Título de "Hechos para ti": muestra el % del próximo hito, o el
  // máximo (20%) si ya se alcanzó — misma dinámica que la barra.
  if (recTitleEl) {
    const recPct = nextTier ? nextTier.pct : lastTier.pct;
    recTitleEl.textContent = `Añade y ahorra ${recPct}%`;
  }

  if (qty >= lastTier.qty) {
    box.classList.add('is-complete');
    if (textEl) textEl.textContent = `✓ ¡Has conseguido el ${lastTier.pct}% de descuento!`;
    return;
  }

  box.classList.remove('is-complete');

  if (!nextTier || !textEl) return;

  const remaining = nextTier.qty - qty;
  const noun = remaining === 1 ? 'artículo más' : 'artículos más';
  textEl.textContent = `¡Añade ${remaining} ${noun} para el ${nextTier.pct}% de descuento!`;
}

// ── SLIDER "HECHOS PARA TI" ──────────────────────────────────
const CD_SLIDE_INTERVAL = 3500;
const CD_COOLDOWN_MS    = 10000;

let cdSliderInterval  = null;
let cdCooldownTimeout = null;
let cdCurrentSlide    = 0;
let cdTotalSlides     = 0;
let cdSliderPaused    = false;

function cdInitSlider() {
  const track = document.getElementById('cd-rec-track');
  if (!track) return;

  const slides = track.querySelectorAll('.cd-rec__slide');
  cdTotalSlides  = slides.length;
  cdCurrentSlide = 0;

  if (cdTotalSlides === 0) return;

  cdGoToSlide(0);
  cdStopSlider();
  cdStartAutoplay();

  // Listeners: SOLO una vez (evita acumularlos en cada apertura del carrito)
  if (track.dataset.cdSliderBound) return;
  track.dataset.cdSliderBound = '1';

  // Pausa al enfocar/usar selects
  track.addEventListener('focus', (e) => {
    if (e.target.classList.contains('cd-rec__select')) cdPauseSlider();
  }, true);

  track.addEventListener('blur', (e) => {
    if (e.target.classList.contains('cd-rec__select')) cdScheduleCooldown();
  }, true);

  track.addEventListener('mousedown', (e) => {
    if (e.target.classList.contains('cd-rec__select')) cdPauseSlider();
  });

  // Puntos clicables
  document.querySelectorAll('.cd-rec__dot').forEach((dot) => {
    dot.addEventListener('click', () => {
      const idx = parseInt(dot.getAttribute('data-index'));
      cdGoToSlide(idx);
      cdPauseSlider();
      cdScheduleCooldown();
    });
  });
}

function cdGoToSlide(index) {
  cdCurrentSlide = index;
  const track = document.getElementById('cd-rec-track');
  if (track) track.style.transform = `translateX(-${index * 100}%)`;

  document.querySelectorAll('.cd-rec__dot').forEach((dot, i) => {
    dot.classList.toggle('active', i === index);
  });
}

function cdStartAutoplay() {
  cdStopSlider();
  cdSliderPaused = false;
  cdSliderInterval = setInterval(() => {
    if (!cdSliderPaused && cdTotalSlides > 1) {
      cdCurrentSlide = (cdCurrentSlide + 1) % cdTotalSlides;
      cdGoToSlide(cdCurrentSlide);
    }
  }, CD_SLIDE_INTERVAL);
}

function cdPauseSlider() {
  cdSliderPaused = true;
  if (cdCooldownTimeout) { clearTimeout(cdCooldownTimeout); cdCooldownTimeout = null; }
}

function cdScheduleCooldown() {
  if (cdCooldownTimeout) clearTimeout(cdCooldownTimeout);
  cdCooldownTimeout = setTimeout(() => {
    cdSliderPaused = false;
    cdCooldownTimeout = null;
  }, CD_COOLDOWN_MS);
}

function cdStopSlider() {
  if (cdSliderInterval)  { clearInterval(cdSliderInterval);  cdSliderInterval  = null; }
  if (cdCooldownTimeout) { clearTimeout(cdCooldownTimeout);  cdCooldownTimeout = null; }
  cdSliderPaused = false;
}

// ── DATOS DE VARIANTES (incrustados, sin fetch) ──────────────
function cdSlideVariants(slide) {
  const tag = slide.querySelector('.cd-rec__variants-data');
  if (!tag) return [];
  try { return JSON.parse(tag.textContent); } catch (e) { return []; }
}

// Coincidencia exacta (todas las opciones elegidas)
function cdMatchVariant(variants, selectedVals) {
  return variants.find(v =>
    Array.isArray(v.options) &&
    v.options.length === selectedVals.length &&
    v.options.every((opt, i) => opt === selectedVals[i])
  );
}

// Coincidencia parcial: respeta solo las opciones ya elegidas
// (ej. solo color elegido → primera variante de ese color, para la imagen)
function cdPartialMatch(variants, selectedVals) {
  const candidates = variants.filter(v =>
    Array.isArray(v.options) &&
    v.options.every((opt, i) => !selectedVals[i] || opt === selectedVals[i])
  );
  if (!candidates.length) return null;
  return candidates.find(v => v.available && v.img) ||
         candidates.find(v => v.img) ||
         candidates.find(v => v.available) ||
         candidates[0];
}

// Nombre de la opción del select (Color, Talla...) = texto del placeholder
function cdOptionLabel(select) {
  const ph = select.querySelector('option[value=""]');
  if (ph && ph.textContent.trim()) return ph.textContent.trim();
  return select.getAttribute('aria-label') || '';
}

// Cambiar imagen del slide con fade (evita re-disparar si es la misma)
function cdSetSlideImage(slide, src) {
  const img = slide.querySelector('.cd-rec__img');
  if (!img || !src) return;
  if (slide.dataset.cdImg === src) return;
  slide.dataset.cdImg = src;
  img.style.opacity = '0';
  setTimeout(() => {
    img.src = src;
    img.style.opacity = '1';
  }, 200);
}

// ── DISCLAIMER ───────────────────────────────────────────────
function cdShowNotice(slide, names) {
  let notice = slide.querySelector('.cd-rec__notice');
  if (!notice) {
    notice = document.createElement('div');
    notice.className = 'cd-rec__notice';
    notice.style.cssText =
      'margin-top:6px;font-size:11px;font-weight:600;color:#d94040;letter-spacing:0.02em;';
    const btn = slide.querySelector('.cd-rec__add');
    if (btn) btn.insertAdjacentElement('beforebegin', notice);
    else slide.querySelector('.cd-rec__info').appendChild(notice);
  }
  notice.textContent = 'Selecciona ' + names.join(' y ');
}

function cdHideNotice(slide) {
  const notice = slide.querySelector('.cd-rec__notice');
  if (notice) notice.remove();
}

// ── VARIANTES — resolver selección al cambiar color/talla ────
function cdInitVariantListeners() {
  const track = document.getElementById('cd-rec-track');
  if (!track) return;
  if (track.dataset.cdVarBound) return;   // solo una vez
  track.dataset.cdVarBound = '1';

  track.addEventListener('change', (e) => {
    if (!e.target.classList.contains('cd-rec__select')) return;

    // (1) Evita que el cambio del selector dispare el update de cantidad del carrito
    e.stopPropagation();
    // (2) Menú cerrado al elegir valor → arranca el cooldown de 10s
    cdScheduleCooldown();

    const slide = e.target.closest('.cd-rec__slide');
    if (!slide) return;

    // Quitar resaltado rojo del select que se acaba de rellenar
    if (e.target.value) e.target.style.borderColor = '';

    const variants    = cdSlideVariants(slide);
    const selects      = Array.from(slide.querySelectorAll('.cd-rec__select'));
    const selectedVals = selects.map(s => s.value);

    // ── IMAGEN: actualizar con coincidencia parcial ──
    // (en cuanto eliges el color cambia, aunque falte la talla)
    const partial = cdPartialMatch(variants, selectedVals);
    if (partial && partial.img) {
      cdSetSlideImage(slide, partial.img);
    }

    // ── Si falta algún valor, no resolvemos variante completa ──
    if (selectedVals.some(v => v === '')) return;

    const variant = cdMatchVariant(variants, selectedVals);
    if (!variant) return;

    cdHideNotice(slide);

    // Guardar el id de la variante correcta
    const hiddenInput = slide.querySelector('.cd-rec__variant-id');
    if (hiddenInput) hiddenInput.value = variant.id;

    // Imagen exacta de la variante (por si difiere de la parcial)
    if (variant.img) cdSetSlideImage(slide, variant.img);

    // Precio de la variante
    const priceEl = slide.querySelector('.cd-rec__price-real');
    if (priceEl && variant.price != null) {
      priceEl.textContent = cdFormatMoney(variant.price);
    }
  });
}

function cdFormatMoney(cents) {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

// ── CLIC EN IMAGEN → IR AL PRODUCTO CON LA SELECCIÓN ─────────
function cdInitImageLinks() {
  document.querySelectorAll('.cd-rec__slide .cd-rec__img').forEach(img => {
    if (img.dataset.cdBound) return;   // evita listeners duplicados
    img.dataset.cdBound = '1';
    img.style.cursor = 'pointer';
    img.addEventListener('click', () => {
      const slide = img.closest('.cd-rec__slide');
      if (!slide) return;

      const baseUrl = slide.getAttribute('data-url');
      if (!baseUrl) return;

      let url = baseUrl;
      const selects = Array.from(slide.querySelectorAll('.cd-rec__select'));

      // Si todas las opciones están elegidas → preseleccionar la variante
      if (selects.length > 0) {
        const selectedVals = selects.map(s => s.value);
        if (!selectedVals.some(v => v === '')) {
          const variant = cdMatchVariant(cdSlideVariants(slide), selectedVals);
          if (variant) {
            url = baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variant.id;
          }
        }
      }

      window.location.href = url;
    });
  });
}

// ── BOTONES AÑADIR ───────────────────────────────────────────
function cdInitAddButtons() {
  document.querySelectorAll('.cd-rec__add').forEach(btn => {
    if (btn.dataset.cdBound) return;   // evita doble listener => se añadían 2 unidades
    btn.dataset.cdBound = '1';
    btn.addEventListener('click', () => {
      const slide = btn.closest('.cd-rec__slide');
      if (!slide) return;

      const selects = Array.from(slide.querySelectorAll('.cd-rec__select'));

      // Producto con variantes: exigir que estén TODAS seleccionadas
      if (selects.length > 0) {
        const missing = selects.filter(s => !s.value);
        if (missing.length > 0) {
          missing.forEach(s => { s.style.borderColor = '#d94040'; });
          selects.filter(s => s.value).forEach(s => { s.style.borderColor = ''; });
          cdShowNotice(slide, missing.map(cdOptionLabel));
          return;
        }
        selects.forEach(s => { s.style.borderColor = ''; });
        cdHideNotice(slide);
      }

      // Resolver id de variante
      const variantInput = slide.querySelector('.cd-rec__variant-id');
      let variantId = variantInput ? variantInput.value : null;

      if (selects.length > 0) {
        const selectedVals = selects.map(s => s.value);
        const variant = cdMatchVariant(cdSlideVariants(slide), selectedVals);
        if (variant) variantId = variant.id;
      }

      if (!variantId) return;

      btn.textContent = '...';
      btn.disabled    = true;

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body:    JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        }),
      })
        .then(r => r.json())
        .then(response => {
          if (response.status) {           // error de Shopify (sin stock, etc.)
            btn.textContent = 'Error';
            btn.disabled    = false;
            return;
          }

          // Actualiza items + subtotal SIN tocar el slider
          cdApplyCartState(response);

          // Feedback en el mismo botón (el slider no se reconstruye)
          btn.textContent = '✓ Añadido';
          btn.classList.add('is-added');
          setTimeout(() => {
            btn.textContent = 'Añadir';
            btn.classList.remove('is-added');
            btn.disabled = false;
          }, 2000);
        })
        .catch(() => {
          btn.textContent = 'Error';
          btn.disabled    = false;
        });
    });
  });
}

// ============================================================
//  ACTUALIZACIÓN QUIRÚRGICA DEL CARRITO
//  Refresca items + subtotal + burbuja sin reconstruir el slider
//  ni el timer. Devuelve true si el carrito quedó vacío.
// ============================================================
function cdApplyCartState(parsedState) {
  if (!parsedState) return false;

  const sections   = parsedState.sections || {};
  const drawerHTML = sections['cart-drawer'];
  const bubbleHTML = sections['cart-icon-bubble'];
  const drawerEl   = document.querySelector('cart-drawer');

  // Burbuja del icono del carrito (siempre)
  if (bubbleHTML) {
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl) {
      const pb = new DOMParser()
        .parseFromString(bubbleHTML, 'text/html')
        .querySelector('.shopify-section');
      if (pb) bubbleEl.innerHTML = pb.innerHTML;
    }
  }

  // ── Carrito VACÍO → re-render completo del interior ─────────
  if (parsedState.item_count === 0) {
    const inner = document.querySelector('cart-drawer .drawer__inner');
    if (inner && drawerHTML) {
      const newInner = new DOMParser()
        .parseFromString(drawerHTML, 'text/html')
        .querySelector('.drawer__inner');
      if (newInner) inner.innerHTML = newInner.innerHTML;
    }
    if (drawerEl) drawerEl.classList.add('is-empty');
    cdStopCountdown();
    cdStopSlider();
    cdClearDeadline(); // carrito vacío → el timer se reinicia al volver a llenarse
    return true;
  }

  if (drawerEl) drawerEl.classList.remove('is-empty');

  if (!drawerHTML) {
    // Sin HTML de respaldo, usamos item_count si existe (mejor que nada)
    if (typeof parsedState.item_count === 'number') {
      cdUpdateIncentiveBar(parsedState.item_count);
    }
    return false;
  }

  const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');

  // ── Barra de descuento por cantidad ──────────────────────────
  // OJO: parsedState.item_count NO existe en la respuesta de
  // /cart/add.js (el botón "Añadir" de recomendados usa ese endpoint,
  // que solo devuelve datos del artículo añadido, no el carrito
  // entero) — por eso leemos el conteo real desde el propio HTML ya
  // renderizado por Liquid (siempre correcto, sea cual sea el
  // endpoint que lo pidió). El elemento vive fuera de #CartDrawer-CartItems
  // así que no se toca por el reemplazo de HTML de abajo — solo recalculamos.
  const freshIncentiveEl = parsed.querySelector('#cd-incentive');
  const freshCount = freshIncentiveEl
    ? parseInt(freshIncentiveEl.dataset.count || '0', 10)
    : (typeof parsedState.item_count === 'number' ? parsedState.item_count : null);
  if (freshCount !== null && !isNaN(freshCount)) {
    cdUpdateIncentiveBar(freshCount);
  }

  // ── 1) Lista de productos: SOLO .cd-items (preserva el slider) ──
  const newItemsList = parsed.querySelector('.cd-items');
  const curItemsList = document.querySelector('cart-drawer .cd-items');

  if (newItemsList && curItemsList) {
    curItemsList.innerHTML = newItemsList.innerHTML;
  } else {
    // Fallback (no esperado): reconstruir el contenedor conservando el slider vivo
    const newContainer = parsed.querySelector('#CartDrawer-CartItems');
    const curContainer = document.querySelector('#CartDrawer-CartItems');
    if (newContainer && curContainer) {
      const liveRec = document.getElementById('cd-rec');
      curContainer.innerHTML = newContainer.innerHTML;
      if (liveRec) {
        const freshRec = curContainer.querySelector('#cd-rec');
        if (freshRec) freshRec.replaceWith(liveRec);
      } else {
        cdInitSlider();
        cdInitVariantListeners();
        cdInitAddButtons();
        cdInitImageLinks();
      }
    }
  }

  // ── 2) Footer (subtotal) ──
  const newFooter = parsed.querySelector('.cd-footer');
  const curFooter = document.querySelector('cart-drawer .cd-footer');
  if (newFooter && curFooter) curFooter.replaceWith(newFooter);

  return false;
}